/**
 * @file Cookie.ts
 * @description Getting, setting and removing cookies with NEXT.JS
 *
 * Can be used on the client side, anywhere.
 * Can be used for server side rendering in getServerSideProps.
 * Can be used in API handlers.
 * Can be used in Next.js 13+.
 *
 * @module Cookie
 */
import { getCookie, setCookie, deleteCookie, getCookies } from 'cookies-next';
import isString from 'lodash/isString';
import { addYears } from 'date-fns';
import { USER_ID, IN_MOBILE_APP, USER_ROLES, OAUTH_TOKEN, LOGGED_IN, ABSMARTLY_ID, ENABLED_MARKETING_COOKIES, ENABLED_ANALYTICAL_COOKIES, COOKIE_PREFERENCES_USED_CTA, HAS_PENDING_COOKIE_CONSENT_SYNC, DONT_SHOW_COOKIE_BAR_AGAIN, } from './constants/cookies';
/**
 * Extracts the domain for cookie storage by removing the subdomain from the hostname.
 * Returns the hostname without the subdomain in the format ".domain.tld".
 *
 * @example
 * // If location.hostname is "www.catawiki.com"
 * // This will return ".catawiki.com"
 *
 * @internal
 * @private
 * This function is intended for internal use only and should not be used directly.
 * It's used by getCookieOptions() to determine the proper domain for cross-subdomain cookies.
 *
 * @returns The cookie domain in the format ".domain.tld"
 */
export const getCookieDomain = () => location.hostname.replace(/^.*?\./, '.');
const getCookieOptions = () => ({
    domain: getCookieDomain(),
    expires: addYears(new Date(), 1),
});
/**
 * Get all cookies as string
 * @example
 *
 * import { Cookie } from '@catawiki/runtime-toolkit';
 *
 * const cookies = Cookie.getCookiesString();
 *
 * expect(cookies).toBe('cookie1=value1; cookie2=value2');
 *
 *
 * @returns The cookies as a string
 *
 */
export const getCookiesString = (context) => {
    const cookies = getCookies(context);
    return Object.entries(cookies)
        .map(([key, value]) => `${key}=${value}`)
        .join('; ');
};
/**
 * Get the user ID from the cookie
 *
 * @example
 *
 * import { Cookie } from '@catawiki/runtime-toolkit';
 *
 * const userId = Cookie.getUserId();
 *
 * @param context - The context object.
 * @returns The user ID if it exists. Otherwise, undefined.
 *
 */
export const getUserId = (context) => {
    const userId = getCookie(USER_ID, context);
    return isString(userId) ? userId : undefined;
};
/**
 * Get the OAuth token from the cookie
 *
 * @example
 *
 * import { Cookie } from '@catawiki/runtime-toolkit';
 *
 * const oAuthToken = Cookie.getOAuthToken();
 *
 * @param context - The context object.
 * @returns The OAuth token if it exists. Otherwise, undefined.
 *
 */
export const getOAuthToken = (context) => {
    const oAuthToken = getCookie(OAUTH_TOKEN, context);
    return isString(oAuthToken) ? oAuthToken : undefined;
};
/**
 * Get the `is mobile app` from the cookie
 *
 * @example
 *
 * import { Cookie } from '@catawiki/runtime-toolkit';
 *
 * const isMobileApp = Cookie.getIsMobileApp();
 *
 * @param context - The context object.
 * @returns The `is mobile app` if it exists. Otherwise, undefined.
 *
 */
export const getIsMobileApp = (context) => {
    const isMobileApp = getCookie(IN_MOBILE_APP, context) === 'true';
    return isMobileApp;
};
/**
 * Get the user roles from the cookie
 *
 * @example
 *
 * import { Cookie } from '@catawiki/runtime-toolkit';
 *
 * const userRoles = Cookie.getUserRoles();
 *
 * @param context - The context object.
 * @returns The user roles if they exist. Otherwise, an empty array.
 *
 */
export const getUserRoles = (context) => {
    const roles = getCookie(USER_ROLES, context);
    return isString(roles) ? roles.split('&') : [];
};
/**
 * Get the user logged in from the cookie `logged_in`
 *
 * @example
 *
 * import { Cookie } from '@catawiki/runtime-toolkit';
 *
 * const userRoles = Cookie.getUserLoggedIn();
 *
 * @param context - The context object.
 * @returns true if the user logged in cookie is `yes`. Otherwise, false.
 *
 */
export const getIsUserLoggedIn = (context) => {
    const isUserLoggedIn = getCookie(LOGGED_IN, context);
    return isUserLoggedIn === 'yes';
};
/**
 *  Get the AB Smartly ID from the cookie
 *
 * @example
 *
 * import { Cookie } from '@catawiki/runtime-toolkit';
 *
 * const abSmartlyId = Cookie.getABSmartlyID();
 *
 * @param context - The context object.
 * @returns The AB Smartly ID if it exists. Otherwise, undefined.
 *
 */
export const getABSmartlyId = (context) => getCookie(ABSMARTLY_ID, context);
/**
 * Check if the marketing cookies preference is set
 *
 * @example
 *
 * import { Cookie } from '@catawiki/runtime-toolkit';
 *
 * const hasPreference = Cookie.hasMarketingCookiesPreference();
 *
 * @param context - The context object.
 * @returns True if the marketing cookies preference exists, false otherwise.
 *
 */
export const hasMarketingCookiesEnabledCookie = (context) => getCookie(ENABLED_MARKETING_COOKIES, context) !== undefined;
/**
 * Get the marketing cookies preference from the cookie
 *
 * @example
 *
 * import { Cookie } from '@catawiki/runtime-toolkit';
 *
 * const marketingEnabled = Cookie.getMarketingCookiesEnabled();
 *
 * @param context - The context object.
 * @returns True if marketing cookies are enabled. Otherwise, false.
 *
 */
export const getMarketingCookiesEnabled = (context) => getCookie(ENABLED_MARKETING_COOKIES, context) === 'true';
/**
 * Set the marketing cookies preference
 *
 * @example
 *
 * import { Cookie } from '@catawiki/runtime-toolkit';
 *
 * Cookie.setMarketingCookiesEnabled(true);
 *
 * @param enabled - Whether marketing cookies should be enabled
 * @param context - The context object
 */
export const setMarketingCookiesEnabled = (enabled, context) => {
    setCookie(ENABLED_MARKETING_COOKIES, enabled.toString(), {
        ...getCookieOptions(),
        ...context,
    });
};
/**
 * Check if the analytical cookies preference is set
 *
 * @example
 *
 * import { Cookie } from '@catawiki/runtime-toolkit';
 *
 * const hasPreference = Cookie.hasAnalyticalCookiesPreference();
 *
 * @param context - The context object.
 * @returns True if the analytical cookies preference exists, false otherwise.
 *
 */
export const hasAnalyticalCookiesEnabledCookie = (context) => getCookie(ENABLED_ANALYTICAL_COOKIES, context) !== undefined;
/**
 * Get the analytical cookies preference from the cookie
 *
 * @example
 *
 * import { Cookie } from '@catawiki/runtime-toolkit';
 *
 * const analyticalEnabled = Cookie.getAnalyticalCookiesEnabled();
 *
 * @param context - The context object.
 * @returns True if analytical cookies are enabled. Otherwise, false.
 *
 */
export const getAnalyticalCookiesEnabled = (context) => getCookie(ENABLED_ANALYTICAL_COOKIES, context) === 'true';
/**
 * Set the analytical cookies preference
 *
 * @example
 *
 * import { Cookie } from '@catawiki/runtime-toolkit';
 *
 * Cookie.setAnalyticalCookiesEnabled(true);
 *
 * @param enabled - Whether analytical cookies should be enabled
 * @param context - The context object
 */
export const setAnalyticalCookiesEnabled = (enabled, context) => {
    setCookie(ENABLED_ANALYTICAL_COOKIES, enabled.toString(), {
        ...getCookieOptions(),
        ...context,
    });
};
/**
 * Get the cookie preferences CTA used indicator from the cookie
 *
 * @example
 *
 * import { Cookie } from '@catawiki/runtime-toolkit';
 *
 * const preferencesUsedCta = Cookie.getCookiePreferencesUsedCta();
 *
 * @param context - The context object.
 * @returns The cookie preference CTA value.
 *
 */
export const getCookiePreferencesUsedCta = (context) => getCookie(COOKIE_PREFERENCES_USED_CTA, context);
/**
 * Set the cookie preferences used CTA value
 *
 * @example
 *
 * import { Cookie } from '@catawiki/runtime-toolkit';
 *
 * Cookie.setCookiePreferencesUsedCta('accept-all');
 *
 * @param value - The CTA value to set
 * @param context - The context object
 */
export const setCookiePreferencesUsedCta = (value, context) => {
    setCookie(COOKIE_PREFERENCES_USED_CTA, value, {
        ...getCookieOptions(),
        ...context,
    });
};
/**
 * Check if there's a pending cookie consent sync
 *
 * @example
 *
 * import { Cookie } from '@catawiki/runtime-toolkit';
 *
 * const hasPendingSync = Cookie.getHasPendingCookieConsentSync();
 *
 * @param context - The context object.
 * @returns True if there is a pending cookie consent sync. Otherwise, false.
 *
 */
export const getHasPendingCookieConsentSync = (context) => getCookie(HAS_PENDING_COOKIE_CONSENT_SYNC, context) === 'true';
/**
 * Set whether there's a pending cookie consent sync
 *
 * @example
 *
 * import { Cookie } from '@catawiki/runtime-toolkit';
 *
 * Cookie.setHasPendingCookieConsentSync(true);
 *
 * @param hasPending - Whether there's a pending cookie consent sync
 * @param context - The context object
 */
export const setHasPendingCookieConsentSync = (hasPending, context) => {
    setCookie(HAS_PENDING_COOKIE_CONSENT_SYNC, hasPending.toString(), {
        ...getCookieOptions(),
        ...context,
    });
};
/**
 * Delete a pending cookie consent sync cookie
 *
 * @example
 *
 * import { Cookie } from '@catawiki/runtime-toolkit';
 *
 * Cookie.deleteHasPendingCookieConsentSync();
 *
 * @param context - The context object
 */
export const deleteHasPendingCookieConsentSync = (context) => {
    deleteCookie(HAS_PENDING_COOKIE_CONSENT_SYNC, {
        ...getCookieOptions(),
        ...context,
    });
};
/**
 * Check if the cookie bar should not be shown again
 *
 * @example
 *
 * import { Cookie } from '@catawiki/runtime-toolkit';
 *
 * const dontShowAgain = Cookie.getDontShowCookieBarAgain();
 *
 * @param context - The context object.
 * @returns True if the cookie bar should not be shown again. Otherwise, false.
 *
 */
export const getDontShowCookieBarAgain = (context) => getCookie(DONT_SHOW_COOKIE_BAR_AGAIN, context) === 'true';
